<template>
  <VDataGridV2
      :loader="dataLoader"
      paginated
      entity-type="order"
      group-children-property="positions"
      :group-context-menu="OrdersService.contextMenu"
      :skeleton-row-count="16"
      @group:dblclick="openOrderCard"
  >
    <VDataGridGroupV2>
      <template #default="{ parent: row }">

        <CommentsButton
          entity-type-name="order"
          :entity="row"
          v-if="$core.currentPartner.isBuyer === true"
        />

        <div>
          <VLink @click.stop="openOrderCard(row)">
            №{{ $core.utils.string.formatWithDelimiter(row.id) }}
          </VLink>
          от {{ $core.utils.date.format(row.createdAt) }}
        </div>

        <span>{{ row.seller.name }}</span>

        <VBadges>
          <VBadge v-if="row.requisites.isSamples">Образцы</VBadge>
          <VBadge v-if="row.requisites.isSamples && row.requisites.isForTestsOnly">Не под план</VBadge>
          <VBadge v-if="!row.requisites.isOfferBased && row.notificationSentAt">Уведомление отправлено {{ $core.utils.date.humanizeDate(row.notificationSentAt, 'HH|mm') }}</VBadge>
        </VBadges>
      </template>
    </VDataGridGroupV2>

    <VDataGridColumnV2
        content-class="comment"
    >
      <template #default="{ row }">
        <CommentsButton entity-type-name="orderPosition" :entity="row" />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="id"
        width="100px"
        label="№"
        filterable
        sortable
        sort-default-value="desc"
        filter-send-name="idPattern"
        :filter-props="idFilterProps"
    >
      <template #default="{ row }">
        {{ $core.utils.string.formatWithDelimiter(row.id) }}

        <VBadges>
          <VBadge v-if="row.isDeleted">Удален</VBadge>
        </VBadges>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="buyersCode"
        label="Арт"
        width="90px"
        content-align="center"
        content-class="ellipsis"
        filterable
        filter-send-name="codePattern"
        :filter-props="codeFilterProps"
    >
      <template #default="{ row }">
        <VLink style="width: 100%" class="ellipsis" @click="OrdersService.showCodeInfoPopup(row, $event.currentTarget)">{{ row.code || '--' }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="name"
        content-class="ellipsis"
        width="350px"
        filterable
        filter-send-name="product"
        filter-identity="*"
        filter-type="select"
        :filter-props="nameFilterProps"
    >
      <template #label>
        Товар
        <div class="description">
          Производитель / материал
        </div>
      </template>
      <template #default="{ row }">
        <span class="ellipsis" :title="row.name">
          {{ row.name }}
        </span>

        <div class="description ellipsis">
          <template v-if="row.isPrinting && row.material">
            Материал: {{ row.material }}
          </template>
          <template v-if="row.isRawMaterialsAndPackage && row.manufacturer">
            Производитель: {{ row.manufacturer.name }}
          </template>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="qty"
        content-align="right"
        width="110px"
    >
      <template #label>
        <div class="nowrap">Кол-во</div>
        <div class="description">Фасовка</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">
          {{ row.qty.format(row.unit) }}
        </div>

        <div
            v-if="row.isRawMaterialsAndPackage && row.packageVolume"
            class="description"
        >
          {{ row.packageVolume.format(row.unit) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="priceFullSeller"
        content-align="right"
        width="110px"
    >
      <template #label>
        Цена товара
        <div class="description">Сумма</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">
          {{ row.priceFullSeller.format(row.currencyCode, row.unit) }}
        </div>
        <div class="description">
          {{ row.sumFullSeller.format(row.currencyCode) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="deliveryType"
        label="Тип доставки"
        header-class="nowrap"
        content-class="nowrap"
        width="140px"
    >
      <template #default="{ row }">
        {{ row.deliveryType.name }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="desiredReceiptDate"
        width="140px"
        label="Дата доставки"
        header-class="nowrap"
        content-class="nowrap"
        filterable
        filter-type="date-range"
        :filter-props="dateFilterProps"
    >
      <template #default="{ row }">
        {{ $core.utils.date.humanizeDate(row.desiredReceiptDate, null) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
        id="plannedShipmentDate"
        width="140px"
        label="Дата отгрузки"
        header-class="nowrap"
        content-class="nowrap"
        filterable
        filter-type="date-range"
        :filter-props="dateFilterProps"
    >
      <template #default="{ row }">
        {{ $core.utils.date.humanizeDate(row.plannedShipmentDate, null) }}
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2 v-if="canCreateDraftOrders">
      <VButton @click="showPrintingOrderCreateDialog">
        Создать черновик полиграфии
      </VButton>

      <VButton @click="showRawMaterialOrderCreateDialog">
        Создать черновик
      </VButton>
    </VDataGridActionBarV2>
  </VDataGridV2>
</template>

<script lang="ts">

import Core                from '@/core/Core';
import OrdersService       from '@/domains/Orders/OrdersService';
import Order               from '@/domains/Orders/Order';
import DataGridLoader      from '@/lib/components/VDataGridV2/DataGridLoader';

import VKebabButton        from "@/lib/components/VKebabButton/VKebabButton.vue";


export default defineComponent({
  name: 'OrdersRegistryExpanded',

  props: {
    /** Режим воронки: показывать черновики заказов вместо оформленных */
    isDraft: {
      type:    Boolean,
      default: false,
    },

    params: {
      type: Object as PropType<Parameters<typeof OrdersService.getOrdersListPaginated>[0]>,
      default: null,
    }
  },

  components: {
    VKebabButton,
  },

  expose: ['refresh'],

  // setup — «чулан» для статики: сервис и константы фильтров,
  // чтобы Vue не оборачивал их в реактивную обёртку
  setup() {
    return {
      OrdersService,
      idFilterProps:         { placeholder: '№', clearable: true },
      codeFilterProps:       { placeholder: 'Артикул', searchable: true, clearable: true },
      nameFilterProps:       { placeholder: 'Название или артикул товара', dataUrl: 'products.getList', searchable: true },
      dateFilterProps:       { placeholder: 'Выберите период', clearable: true },
    };
  },

  data() {
    return {
      dataLoader: new DataGridLoader(OrdersService.getOrdersListPaginated)
          .useExtraParams({ isDraft: this.isDraft })
          .useReactiveSource(() => {
            return {
              ...this.params
            }
          }),
    };
  },

  computed: {
    canCreateDraftOrders() {
      return Core.currentPartner?.isLSZ;
    },
  },

  methods: {
   async openOrderCard(row: Order): Promise<void> {
      await OrdersService.showOrderCard(row);
      this.dataLoader.refresh();
    },

    async showPrintingOrderCreateDialog(): Promise<void> {
      await OrdersService.showOrderCard(null, 'printing', null, null, this.params);
      this.dataLoader.refresh();
    },

    async showRawMaterialOrderCreateDialog(): Promise<void> {
      await OrdersService.showOrderCard(null, 'rawMaterialsAndPackage', null, null, this.params);
      this.dataLoader.refresh();
    },

    refresh(): void {
      this.dataLoader.refresh();
    },
  },
});
</script>
